import { useEffect, useId, useState } from 'react';
import { CustomModal } from '../CustomModal';
import { Btn } from '../ui';

type CatalogUnit = {
    id: string;
    name: string;
};

interface DeleteUnitOfMeasureModalProps {
    item: CatalogUnit | null;
    usageLabel: string;
    usedByCount: number;
    isDeleting: boolean;
    onDismiss: () => void;
    onConfirmDelete: () => void;
}

export const DeleteUnitOfMeasureModal = ({
    item,
    usageLabel,
    usedByCount,
    isDeleting,
    onDismiss,
    onConfirmDelete,
}: DeleteUnitOfMeasureModalProps) => {
    const visible = Boolean(item?.id);
    const descriptionId = useId();
    const [confirmReady, setConfirmReady] = useState(false);
    const inUse = usedByCount > 0;
    const name = item?.name?.trim() || 'this unit of measure';

    useEffect(() => {
        if (!visible) {
            setConfirmReady(false);
            return;
        }
        setConfirmReady(false);
        const timer = window.setTimeout(() => setConfirmReady(true), 400);
        return () => window.clearTimeout(timer);
    }, [visible, item?.id]);

    return (
        <CustomModal
            onDismiss={onDismiss}
            visible={visible}
            header="Delete unit of measure?"
            describedById={descriptionId}
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonDisabled={isDeleting}
                        buttonOnClick={onDismiss}
                    />
                    <Btn
                        variant="danger"
                        buttonText={isDeleting ? 'Deleting...' : 'Delete'}
                        buttonDisabled={isDeleting || !confirmReady || inUse}
                        buttonOnClick={onConfirmDelete}
                    />
                </div>
            }
        >
            <div id={descriptionId}>
                <p>
                    You are about to permanently delete <strong>“{name}”</strong> from the Units of Measure
                    list. This cannot be undone.
                </p>
                {inUse ? (
                    <p>
                        This unit of measure is used by {usageLabel}. Change those first. The catalog row
                        stays until then.
                    </p>
                ) : (
                    <p>Nothing is using this unit of measure.</p>
                )}
            </div>
        </CustomModal>
    );
};
